import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { ApiError, errorMessage } from '@/api/errors';
import { register } from '@/api/endpoints/auth';
import { useAuth } from '@/auth/useAuth';
import { AuthLayout } from './AuthLayout';

export function SignUpPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldErrors({});
    setError(null);

    if (!consent) {
      setError('Please accept the privacy notice to create an account.');
      return;
    }
    if (form.password !== form.confirm) {
      setFieldErrors({ confirm: 'The two passwords do not match.' });
      return;
    }
    if (form.password.length < 8) {
      setFieldErrors({ password: 'Use at least 8 characters.' });
      return;
    }

    setBusy(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        consent: true,
        ...(form.phone ? { phone: form.phone } : {}),
      });
      // Sign in straight away — a new account with no vehicle has nothing to show
      // until it reaches the setup flow, so bouncing back to a login form is friction.
      await signIn(form.email, form.password);
      navigate('/setup', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.issues.length > 0) {
        setFieldErrors(
          Object.fromEntries(caught.issues.map((issue) => [issue.field, issue.message]))
        );
      }
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Set up crash detection for your vehicle."
      footer={
        <>
          Already registered?{' '}
          <Link to="/login" className="font-medium text-accent hover:text-accent-hover">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <TextField
          label="Full name"
          autoComplete="name"
          required
          autoFocus
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          error={fieldErrors.name}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          error={fieldErrors.email}
        />
        <TextField
          label="Mobile number"
          type="tel"
          autoComplete="tel"
          placeholder="+91…"
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
          error={fieldErrors.phone}
          hint="Where emergency SMS and calls will be sent after a crash."
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={(event) => update('password', event.target.value)}
          error={fieldErrors.password}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          required
          value={form.confirm}
          onChange={(event) => update('confirm', event.target.value)}
          error={fieldErrors.confirm}
        />

        <label className="flex items-start gap-2 text-[13px] text-ink-secondary">
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
            className="mt-0.5 size-4 accent-accent"
          />
          <span>
            I have read the{' '}
            <Link to="/privacy" target="_blank" className="font-medium text-accent hover:text-accent-hover">
              privacy notice
            </Link>{' '}
            and agree to AutoAware processing my details, my vehicle location and my
            emergency contact number to detect crashes and send alerts.
          </span>
        </label>

        {error && (
          <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
